/** Pure helpers for invite links, room codes and invitation status. */

export const JOIN_PATH_PREFIX = '/join/';
export const PUBLIC_WEB_ORIGIN = 'https://app.bhabi.ca';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isRoomId = (value: string): boolean => UUID_RE.test(value);

/**
 * Accepts whatever a friend pasted — a full invite URL, a `/join/<id>` path,
 * or the bare room id — and returns the room id, or null when it cannot be
 * a valid table.
 */
export const parseRoomInput = (raw: string): string | null => {
  const input = (raw || '').trim();
  if (!input) return null;

  const joinIdx = input.indexOf(JOIN_PATH_PREFIX);
  let candidate = input;
  if (joinIdx >= 0) {
    candidate = input.slice(joinIdx + JOIN_PATH_PREFIX.length);
  }
  candidate = candidate.split(/[?#/\s]/)[0];

  return isRoomId(candidate) ? candidate.toLowerCase() : null;
};

export const buildInvitePath = (roomID: string) =>
  `${JOIN_PATH_PREFIX}${encodeURIComponent(roomID)}`;

export const buildInviteUrl = (origin: string, roomID: string) =>
  `${origin.replace(/\/+$/, '')}${buildInvitePath(roomID)}`;

/** Short, human friendly table code shown next to the link (display only). */
export const shortTableCode = (roomID: string) =>
  roomID.replace(/-/g, '').slice(0, 6).toUpperCase();

export type InvitationStatus = 'waiting' | 'playing' | 'concluded';

export interface Invitation {
  roomID: string;
  numberOfPlayers: number;
  joinedPlayers: number;
  status: InvitationStatus;
}

export type InvitationProblem = 'full' | 'in-progress' | 'finished' | null;

/**
 * Why a visitor cannot take a seat, or null when they can. Members of the
 * table can always rejoin a game that is in progress.
 */
export const invitationProblem = (
  inv: Invitation,
  { isMember }: { isMember: boolean }
): InvitationProblem => {
  if (isMember) return null;
  if (inv.status === 'concluded') return 'finished';
  if (inv.status === 'playing') return 'in-progress';
  if (inv.joinedPlayers >= inv.numberOfPlayers) return 'full';
  return null;
};

export const PROBLEM_COPY: Record<
  Exclude<InvitationProblem, null>,
  { title: string; body: string }
> = {
  full: {
    title: 'This table is full',
    body: 'Every seat has been taken. Ask the host to start a new table and send you the fresh link.',
  },
  'in-progress': {
    title: 'The game has already started',
    body: 'Cards are already dealt at this table. If you were seated here before, try rejoining; otherwise ask for a new link.',
  },
  finished: {
    title: 'This game has finished',
    body: 'The table has closed. Start your own table and invite everyone back.',
  },
};

/** Hosts whose `https` links iOS hands to the app (Associated Domains). */
export const UNIVERSAL_LINK_HOSTS = ['app.bhabi.ca'] as const;
/** Custom URL scheme registered in Info.plist (`bhabi://join/<id>`). */
export const CUSTOM_URL_SCHEME = 'bhabi';

const JOIN_ROUTE_RE = /^\/join\/([^/]+)\/?$/;

/**
 * Map an OS-delivered URL to an in-app route, or null when it is not an
 * invite we recognise. Accepted forms:
 *   https://app.bhabi.ca/join/<uuid>       (universal link)
 *   bhabi://join/<uuid>                    (custom scheme)
 *   bhabi:///join/<uuid>, bhabi:join/<uuid>
 * Query strings and fragments are ignored; the room id is lower-cased.
 */
export const deepLinkToPath = (
  url: string | null | undefined
): string | null => {
  const raw = (url || '').trim();
  if (!raw) return null;

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }

  const scheme = parsed.protocol.replace(/:$/, '').toLowerCase();
  let path: string;
  if (scheme === 'https') {
    const host = parsed.hostname.toLowerCase();
    if (!(UNIVERSAL_LINK_HOSTS as readonly string[]).includes(host)) {
      return null;
    }
    path = parsed.pathname;
  } else if (scheme === CUSTOM_URL_SCHEME) {
    // `bhabi://join/<id>` parses as host "join" + path "/<id>".
    path = `/${parsed.host}/${parsed.pathname}`;
  } else {
    return null;
  }

  let decoded: string;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    return null;
  }
  const match = decoded.replace(/\/{2,}/g, '/').match(JOIN_ROUTE_RE);
  if (!match || !isRoomId(match[1])) return null;
  return buildInvitePath(match[1].toLowerCase());
};
